import { useMemo } from 'react';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Caption, ThemedText } from '@/components/ui/Text';
import { Card } from '@/components/ui/Card';
import { AppImage } from '@/components/ui/AppImage';
import { useTheme } from '@/theme';
import type { TripSummary } from '@/services/tripSummary';
import { buildTripMapSnapshotUrl } from '@/utils/tripMapSnapshot';

type TripSummaryMapPreviewProps = {
  summary: TripSummary;
};

export function TripSummaryMapPreview({ summary }: TripSummaryMapPreviewProps) {
  const theme = useTheme();
  const snapshotUrl = useMemo(
    () => buildTripMapSnapshotUrl(summary.mappedPlaces),
    [summary.mappedPlaces],
  );

  if (!snapshotUrl) {
    return null;
  }

  return (
    <Card
      variant="outlined"
      style={{
        padding: theme.spacing(1.25),
        backgroundColor: theme.colors.backgroundSecondary,
        borderColor: theme.colors.borderMuted,
        marginTop: theme.spacing(1),
      }}
    >
      <View style={{ gap: theme.spacing(0.85) }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
          <View
            style={{
              width: 28,
              height: 28,
              borderRadius: theme.radii.pill,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme.colors.primary + '16',
            }}
          >
            <Ionicons name="map-outline" size={16} color={theme.colors.primary} />
          </View>
          <ThemedText variant="bodyStrong" style={{ fontSize: 15 }}>
            Places visited
          </ThemedText>
        </View>

        <View
          style={{
            height: 168,
            borderRadius: theme.radii.lg,
            overflow: 'hidden',
            borderWidth: 1,
            borderColor: theme.colors.borderMuted,
            backgroundColor: theme.colors.surface,
          }}
        >
          <AppImage
            source={{ uri: snapshotUrl }}
            resizeMode="cover"
            style={{ width: '100%', height: '100%' }}
          />
        </View>
        <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
          {`Showing ${Math.min(summary.mappedPlaces.length, 12)} pinned stop${summary.mappedPlaces.length === 1 ? '' : 's'} from your itinerary`}
        </Caption>
      </View>
    </Card>
  );
}
